import { ColumnDef } from '@tanstack/react-table';
import { useDetailsBookingsVM } from './useDetailsBookingsVM';
import { MutationTable } from '@components/index';
import { BookingTable } from '@services/member/booking/booking.types';
import { UsePaginationMutation } from '@services/index';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { Pdf, ShopsSettingsLine, Warning } from '@assets/images/icons';
import { Button, Icon } from '@design-system/index';
import { formatMoney } from '@utils/numbers';

interface DetailsBookingsProps {
    mutationHook: UsePaginationMutation<BookingTable>;
    /** Tri par défaut sur la date d'arrivée : 'asc' pour les listes à venir, 'desc' pour l'historique */
    defaultDirection?: 'asc' | 'desc';
}

export default function DetailsBookings({ mutationHook, defaultDirection = 'desc' }: DetailsBookingsProps) {
    const { id, bookingColumnHelper } = useDetailsBookingsVM();
    const navigate = useNavigate();

    const contributionsColumns = [
        bookingColumnHelper.accessor('member.cosemNumber', {
            header: 'N° COSEM',
            id: 'member.cosemNumber',
            enableSorting: true,
            cell: (info) => (
                <div style={{ display: 'flex', gap: 5, alignItems: 'center' }}>
                    {info.row.original.paymentInPending && (
                        <Icon icon={<Warning />} size={18} color="error" />
                    )}
                    <>{info.getValue()}</>
                </div>
            ),
            meta: {
                clickable: true
            }
        }),
        bookingColumnHelper.accessor((row) => row.member.personalInformation.lastname + ' ' + row.member.personalInformation.firstname, {
            id: 'member.personalInformation.lastname',
            header: 'Adhérent',
            enableSorting: true,
            cell: (info) => <>{info.getValue().toUpperCase()}</>,
            meta: {
                clickable: true
            }
        }),
        bookingColumnHelper.accessor('requestDate', {
            header: 'Demandé le',
            enableSorting: true,
            cell: (info) => <>{moment(info.getValue()).format('DD/MM/YYYY')} </>,
            meta: {
                clickable: true
            }
        }),
        bookingColumnHelper.accessor((row) => row.rentalPrice, {
            id: 'rentalPrice',
            header: 'Prix',
            enableSorting: true,
            meta: {
                clickable: true
            },
            cell: (info) => <>{formatMoney(info.getValue())}</>
        }),
        bookingColumnHelper.accessor('arrivalDate', {
            header: 'Arrivée',
            enableSorting: true,
            cell: (info) => <>{moment(info.getValue()).format('DD/MM/YYYY')} </>,
            meta: {
                clickable: true
            }
        }),
        bookingColumnHelper.accessor('departureDate', {
            header: 'Départ',
            enableSorting: true,
            cell: (info) => <>{moment(info.getValue()).format('DD/MM/YYYY')} </>,
            meta: {
                clickable: true
            }
        }),
        bookingColumnHelper.display({
            id: 'action',
            size: 0,
            header: 'Documents',
            meta: {
                style: {
                    textAlign: 'center'
                }
            },
            cell: (info) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center', gap: '12px' }}>
                        {info.row.original.confirmationPdfLink && (
                            <Button
                                variant="icon"
                                color="primary"
                                size="small"
                                icon={<Icon icon={<ShopsSettingsLine />} size={20} color="primary" />}
                                hoverIcon={<Icon icon={<ShopsSettingsLine />} size={20} color="secondary" />}
                                onClick={() => {
                                    window.open(info.row.original.confirmationPdfLink, '_blank');
                                }}
                            />
                        )}
                        {info.row.original.pdfLink && (
                            <Button
                                variant="icon"
                                color="primary"
                                size="small"
                                icon={<Icon icon={<Pdf />} size={20} color="primary" />}
                                hoverIcon={<Icon icon={<Pdf />} size={20} color="secondary" />}
                                onClick={() => {
                                    window.open(info.row.original.pdfLink, '_blank');
                                }}
                            />
                        )}
                    </div>
                );
            }
        })
    ] as ColumnDef<BookingTable>[];

    return (
        <MutationTable
            columns={contributionsColumns}
            mutationHook={mutationHook}
            mutationHookParams={{ id }}
            onRowClick={(row) => {
                navigate(`/members-management/apartments/${id}/bookings/${row.id}/edit`);
            }}
            className="no-card"
            selectable={false} //TODO, false cause ui wrong alignment of pagination
            deleting={{
                enabled: true,
                entity: 'apartment_booking'
            }}
            defaultSorting={{
                field: 'arrivalDate',
                direction: defaultDirection
            }}
        />
    );
}
